type FormStop = { id?: string; provisoryId?: string };
type StopReference = { pickupId: string; dropoffId: string | null };

// After a save that keeps the dialog open, the form's stops take the ids the
// server gave them. Stop units and CMR configs reference a new stop by its
// provisoryId, so those references have to move to the saved id as well: the
// next save would otherwise send the stop as an existing one (it has an id
// now) while still referencing it by a provisoryId the server no longer maps,
// and the raw provisoryId reaches the database (DORA-220, SERVER-13S).
// Saved stops are matched to form stops by position, as the server stores
// them in the order they were sent.
//
// CMR configs likewise take the ids the server stored them under: a config
// added in the dialog has a temporary client-side id, which the server never
// keeps, so downloading it by that id would fail (DORA-481, SERVER-15D). They
// are matched by their (already remapped) pickup/dropoff pair, which is unique
// per cargo.
export const adoptSavedStopIds = <
  S extends FormStop,
  U extends StopReference,
  C extends StopReference & { id: string },
>(
  form: {
    pickupList: S[];
    dropoffList: S[];
    stopUnits: U[];
    cmrConfigs: C[];
  },
  saved: {
    pickupList: { id: string }[];
    dropoffList: { id: string }[];
    cmrConfigs: ({ id: string } & StopReference)[];
  }
) => {
  const savedIdByReference = new Map<string, string>();

  const adoptIds = (stops: S[], savedStops: { id: string }[]): S[] =>
    stops.map((stop, i) => {
      const savedId = savedStops[i]?.id;
      if (!savedId) return stop;
      if (stop.id) savedIdByReference.set(stop.id, savedId);
      if (stop.provisoryId) savedIdByReference.set(stop.provisoryId, savedId);
      return { ...stop, id: savedId, provisoryId: undefined };
    });

  const pickupList = adoptIds(form.pickupList, saved.pickupList);
  const dropoffList = adoptIds(form.dropoffList, saved.dropoffList);

  const remap = <R extends StopReference>(reference: R): R => ({
    ...reference,
    pickupId: savedIdByReference.get(reference.pickupId) ?? reference.pickupId,
    dropoffId: reference.dropoffId
      ? savedIdByReference.get(reference.dropoffId) ?? reference.dropoffId
      : reference.dropoffId,
  });

  const adoptSavedCmrConfigId = (config: C): C => {
    const savedConfig = saved.cmrConfigs.find(
      (c) => c.pickupId === config.pickupId && c.dropoffId === config.dropoffId
    );
    return savedConfig ? { ...config, id: savedConfig.id } : config;
  };

  return {
    pickupList,
    dropoffList,
    stopUnits: form.stopUnits.map(remap),
    cmrConfigs: form.cmrConfigs.map(remap).map(adoptSavedCmrConfigId),
  };
};

// Whether a form has rows that only get their ids from the saved cargo: a
// stop added in the form (no id yet), or a CMR config under a temporary id
// the server didn't keep.
export const hasRowsAwaitingIds = (
  form: {
    pickupList: FormStop[];
    dropoffList: FormStop[];
    cmrConfigs: { id: string }[];
  },
  saved: { cmrConfigs: { id: string }[] }
) =>
  [...form.pickupList, ...form.dropoffList].some((stop) => !stop.id) ||
  form.cmrConfigs.some(
    (config) => !saved.cmrConfigs.some((s) => s.id === config.id)
  );
